import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, BladApi } from '../api/client';
import { useLista, type WynikListy } from '../api/lista';
import type { Lokalizacja, RodzajLokalizacji } from '../api/typy';
import { useAuth } from '../auth/AuthContext';
import {
  Karta, Komunikat, Ladowanie, Plakietka, Pole, PoleTekstowe, Przelacznik, Przycisk, Pusto, Wybor,
} from '../components/ui';
import { PasekWspolpracy } from '../components/PasekWspolpracy';
import { nazwaZListy, useEdycjaWspolna } from '../lib/wspolna-edycja';
import { PrzyciskExcel } from '../components/PrzyciskExcel';

const RODZAJE: Record<RodzajLokalizacji, string> = {
  BUDYNEK: 'Budynek', PIETRO: 'Piętro', SALA: 'Sala',
  STREFA: 'Strefa', SZAFA: 'Szafa', POLKA: 'Półka',
};

/** Głębokość w drzewie liczona ze ścieżki — do wcięcia na liście. */
const poziom = (l: Lokalizacja) => (l.path?.split(' / ').length ?? 1) - 1;

export function Lokalizacje() {
  const { maUprawnienie } = useAuth();
  const mozeEdytowac = maUprawnienie('lokalizacje.zapis');
  const [szukaj, setSzukaj] = useState('');
  const [edytowana, setEdytowana] = useState<Lokalizacja | 'nowa' | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const qc = useQueryClient();

  const lista = useLista({
    queryKey: ['lokalizacje', szukaj],
    queryFn: () => api.get<{ pozycje: Lokalizacja[] }>(
      `/lokalizacje${szukaj ? `?szukaj=${encodeURIComponent(szukaj)}` : ''}`,
    ),
  });

  const usun = useMutation({
    mutationFn: (id: number) => api.del(`/lokalizacje/${id}`),
    onSuccess: () => { setBlad(null); void qc.invalidateQueries({ queryKey: ['lokalizacje'] }); },
    onError: (e) => setBlad(e instanceof BladApi ? e.message : 'Nie udało się usunąć.'),
  });

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Lokalizacje</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Budynek → piętro → sala → szafa → półka. Sale zajęciowe to te same rekordy co miejsca składowania.
          </p>
        </div>
        <PrzyciskExcel dzial="lokalizacje" parametry={new URLSearchParams(szukaj ? { szukaj } : {})} className="ml-auto" />
        {mozeEdytowac && (
          <Przycisk wariant="glowny" onClick={() => setEdytowana('nowa')}>Nowa lokalizacja</Przycisk>
        )}
      </div>

      <div className="max-w-md">
        <Pole etykieta="Szukaj" value={szukaj} onChange={(e) => setSzukaj(e.target.value)}
          placeholder="Kod, nazwa albo fragment ścieżki" />
      </div>

      {blad && <Komunikat rodzaj="blad">{blad}</Komunikat>}

      {edytowana && (
        <FormularzLokalizacji
          key={edytowana === 'nowa' ? 'nowa' : edytowana.id}
          lokalizacja={edytowana === 'nowa' ? null : edytowana}
          wszystkie={lista}
          zamknij={() => setEdytowana(null)}
        />
      )}

      {lista.wczytywanie ? <Ladowanie /> : (
        <Karta>
          {lista.pozycje.length > 0 ? (
            <ul className="divide-y divide-kamien-200">
              {lista.pozycje.map((l) => (
                <li key={l.id} className="flex items-center gap-3 px-5 py-3">
                  <span style={{ paddingLeft: `${poziom(l) * 18}px` }} className="flex-1">
                    <span className="font-mono text-xs text-kamien-500">{l.code}</span>
                    <Link to={`/lokalizacje/${l.id}`}
                      className={`ml-3 text-sm hover:text-bordo-600 hover:underline ${l.isActive ? 'text-kamien-900' : 'text-kamien-400'}`}>
                      {l.name}
                    </Link>
                    <span className="ml-2 text-xs text-kamien-400">{RODZAJE[l.kind]}</span>
                  </span>

                  <div className="flex flex-wrap items-center gap-1.5">
                    {!l.isActive && <Plakietka>wyłączona</Plakietka>}
                    {l.isClassroom && <Plakietka ton="ok">sala zajęciowa</Plakietka>}
                    {!l.isStorage && <Plakietka>bez składowania</Plakietka>}
                    {l._count.equipmentUnits > 0 && <Plakietka>{l._count.equipmentUnits} egz.</Plakietka>}
                  </div>

                  {mozeEdytowac && (
                    <div className="flex gap-1">
                      <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs"
                        onClick={() => setEdytowana(l)}>Edytuj</Przycisk>
                      <Przycisk wariant="cichy" className="min-h-9 px-2 text-xs"
                        onClick={() => usun.mutate(l.id)}>Usuń</Przycisk>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <Pusto
              tytul="Brak lokalizacji"
              opis="Zacznij od budynku, potem dodawaj piętra, sale i miejsca składowania."
            />
          )}
        </Karta>
      )}
    </div>
  );
}

const PUSTA = {
  code: '', name: '', kind: 'SALA' as RodzajLokalizacji, parentId: '',
  isStorage: true, isClassroom: false, isActive: true, notes: '',
};

function FormularzLokalizacji({ lokalizacja, wszystkie, zamknij }: {
  lokalizacja: Lokalizacja | null; wszystkie: WynikListy<Lokalizacja>; zamknij: () => void;
}) {
  const qc = useQueryClient();
  const edycja = useEdycjaWspolna<Lokalizacja, typeof PUSTA>({
    encja: 'lokalizacje', rekord: lokalizacja, pusty: PUSTA,
    doFormularza: (l) => ({
      code: l.code, name: l.name, kind: l.kind,
      parentId: String(l.parentId ?? ''),
      isStorage: l.isStorage, isClassroom: l.isClassroom,
      isActive: l.isActive, notes: l.notes ?? '',
    }),
    wczytaj: (id) => api.get<Lokalizacja>(`/lokalizacje/${id}`),
    etykiety: {
      code: 'Kod', name: 'Nazwa', kind: 'Rodzaj', parentId: 'Lokalizacja nadrzędna',
      isStorage: 'Miejsce składowania', isClassroom: 'Sala zajęciowa', isActive: 'Aktywna', notes: 'Uwagi',
    },
    pokazWartosc: (pole, v) => (pole === 'parentId'
      ? nazwaZListy(wszystkie.pozycje, v, (l) => l.path ?? l.name) : undefined),
  });
  const { dane, ustaw: setDane } = edycja;
  const [bledy, setBledy] = useState<Record<string, string>>({});
  const [blad, setBlad] = useState<string | null>(null);

  const zapisz = useMutation({
    mutationFn: () => edycja.zapisz((d, wersja) => {
      const cialo = {
        ...d,
        parentId: d.parentId || null,
        ...(lokalizacja ? { version: wersja } : {}),
      };
      return lokalizacja
        ? api.put<Lokalizacja>(`/lokalizacje/${lokalizacja.id}`, cialo)
        : api.post<Lokalizacja>('/lokalizacje', cialo);
    }),
    onSuccess: (zapisana) => {
      void qc.invalidateQueries({ queryKey: ['lokalizacje'] });
      edycja.poZapisie(zapisana, zamknij);
    },
    onError: (e) => {
      if (e instanceof BladApi) {
        setBledy(e.bledyPol);
        if (Object.keys(e.bledyPol).length === 0) setBlad(e.message);
      }
    },
  });

  // Własne poddrzewo odpada z listy rodziców — inaczej powstałaby pętla.
  const kandydaci = wszystkie.pozycje.filter(
    (l) => !lokalizacja || (l.id !== lokalizacja.id && !l.path?.includes(lokalizacja.name)),
  );

  return (
    <Karta tytul={lokalizacja ? `Lokalizacja ${lokalizacja.code}` : 'Nowa lokalizacja'}
      opis={lokalizacja?.path ?? undefined}>
      <form
        className="grid gap-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(e) => { e.preventDefault(); setBlad(null); setBledy({}); zapisz.mutate(); }}
      >
        {blad && <div className="sm:col-span-2 lg:col-span-3"><Komunikat rodzaj="blad">{blad}</Komunikat></div>}
        <PasekWspolpracy edycja={edycja} />

        <Pole etykieta="Kod" value={dane.code} blad={bledy.code}
          onChange={(e) => setDane({ ...dane, code: e.target.value })}
          podpowiedz="Np. A-01, B-2, 214" required />
        <Pole etykieta="Nazwa" value={dane.name} blad={bledy.name}
          onChange={(e) => setDane({ ...dane, name: e.target.value })} required />
        <Wybor etykieta="Rodzaj" value={dane.kind} blad={bledy.kind}
          onChange={(e) => setDane({ ...dane, kind: e.target.value as RodzajLokalizacji })}>
          {Object.entries(RODZAJE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </Wybor>

        <div className="sm:col-span-2 lg:col-span-3">
          <Wybor etykieta="Lokalizacja nadrzędna" value={dane.parentId}
            blad={bledy.parentId ?? wszystkie.blad}
            onChange={(e) => setDane({ ...dane, parentId: e.target.value })}>
            <option value="">— najwyższy poziom —</option>
            {kandydaci.map((l) => (
              <option key={l.id} value={l.id}>{l.path ?? l.name}</option>
            ))}
          </Wybor>
        </div>

        <div className="sm:col-span-2 lg:col-span-3">
          <PoleTekstowe etykieta="Uwagi" value={dane.notes} blad={bledy.notes}
            onChange={(e) => setDane({ ...dane, notes: e.target.value })} />
        </div>

        <div className="flex flex-col gap-1 sm:col-span-2 lg:col-span-3">
          <Przelacznik etykieta="Miejsce składowania"
            opis="Można tu trzymać stan magazynowy i spisywać z natury"
            checked={dane.isStorage}
            onChange={(e) => setDane({ ...dane, isStorage: e.target.checked })} />
          <Przelacznik etykieta="Sala zajęciowa"
            opis="Można tu prowadzić zajęcia i rezerwować na nie sprzęt"
            checked={dane.isClassroom}
            onChange={(e) => setDane({ ...dane, isClassroom: e.target.checked })} />
          <Przelacznik etykieta="Lokalizacja czynna"
            checked={dane.isActive}
            onChange={(e) => setDane({ ...dane, isActive: e.target.checked })} />
        </div>

        <div className="flex gap-3 sm:col-span-2 lg:col-span-3">
          <Przycisk type="submit" wariant="glowny" disabled={zapisz.isPending}>
            {zapisz.isPending ? 'Zapisywanie...' : lokalizacja ? 'Zapisz zmiany' : 'Dodaj lokalizację'}
          </Przycisk>
          <Przycisk type="button" wariant="cichy" onClick={zamknij}>Anuluj</Przycisk>
        </div>
      </form>
    </Karta>
  );
}
